import React, { useRef, useMemo, useState, useEffect } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps } from 'formik';
import 'formik';
import { get, kebabCase, pick } from 'lodash';
import moment from 'moment';
import {
  focusNextInput,
  getInputClasses,
  getValidationStatus,
} from 'Components/frameworks/formik/inputsUtils';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { BirthDateInputSeparator } from './BirthDateInputSeparator';
import { ErrorNote } from './ErrorNote';
import { Legend } from './Legend';
type Props = FieldProps & {
  air360ClassName: string;
  className?: string;
  dataTestId?: string;
  dateFormat: string;
  dayPlaceholder: string;
  hideErrorMessage: boolean;
  isDisabled: boolean;
  isRequired: boolean;
  label?: string;
  labelAdditionalInfo?: string;
  monthPlaceholder: string;
  yearPlaceholder: string;
  errorMessage?: string;
};
export const DateInput = ({
  air360ClassName,
  className,
  dataTestId,
  dateFormat,
  dayPlaceholder,
  field,
  form,
  hideErrorMessage,
  isDisabled,
  isRequired,
  label,
  labelAdditionalInfo,
  monthPlaceholder,
  onKeyDown,
  yearPlaceholder,
  errorMessage,
  ...props
}: Props) => {
  const { isValidated, isErrored, isDefaultState } = getValidationStatus(field, form, isDisabled);
  const { current: uniqIdentifier } = useRef(`dateInput-${field.name}`);
  const refFirstInput = useRef(null);
  const errorNoteId = `error-${kebabCase(field.name)}`;
  // Form state (global)
  const date = useMemo(() => (field.value ? moment(field.value) : null), [field.value]);
  const dateDay = date && date.isValid() ? date.format('DD') : '';
  const dateMonth = date && date.isValid() ? date.format('MM') : '';
  const dateYear = date && date.isValid() ? date.format('YYYY') : '';
  // Field state (local)
  const [day, setDay] = useState(dateDay);
  const [month, setMonth] = useState(dateMonth);
  const [year, setYear] = useState(dateYear);

  const onChangeDay = ({ target }) => {
    setDay(target.value);
    focusNextInput(target);
  };

  const onChangeMonth = ({ target }) => {
    setMonth(target.value);
    focusNextInput(target);
  };

  const onChangeYear = ({ target }) => {
    setYear(target.value);
    focusNextInput(target);
  };

  const structure = [
    {
      keyPos: 'd',
      name: `${field.name}-BirthdateInput-day`,
      value: day || dateDay,
      title: 'Day',
      length: 2,
      handleChange: onChangeDay,
      placeholder: dayPlaceholder,
    },
    {
      keyPos: 'm',
      name: `${field.name}-BirthdateInput-month`,
      value: month || dateMonth,
      title: 'Month',
      length: 2,
      handleChange: onChangeMonth,
      placeholder: monthPlaceholder,
    },
    {
      keyPos: 'y',
      name: `${field.name}-BirthdateInput-year`,
      value: year || dateYear,
      title: 'Year',
      length: 4,
      handleChange: onChangeYear,
      placeholder: yearPlaceholder,
    },
  ].sort(
    // sort each input to respect the locale format (DD/MM/YYYY or YYYY/MM/DD ...)
    // Normalize format: JJ -> DD, AAAA -> YYYY to support both formats
    (i1, i2) => {
      const normalizedFormat = dateFormat
        .toLowerCase()
        .replace(/j/g, 'd') // Replace 'j' (JJ) with 'd' (DD)
        .replace(/a/g, 'y');
      // Replace 'a' (AAAA) with 'y' (YYYY)
      return normalizedFormat.indexOf(i1.keyPos) - normalizedFormat.indexOf(i2.keyPos);
    },
  );
  // Update form state on field change
  useEffect(() => {
    if (structure.every(({ value, length }) => value.length === length)) {
      const birthdate = moment([+year, +month - 1, +day]).valueOf();
      form.setFieldValue(field.name, birthdate);
    } else {
      form.setFieldValue(field.name, null);
    }
  }, [day, month, year]);
  useEffect(() => {
    if (day !== dateDay) {
      setDay(dateDay);
    }

    if (month !== dateMonth) {
      setMonth(dateMonth);
    }

    if (year !== dateYear) {
      setYear(dateYear);
    }
  }, [dateDay, dateMonth, dateYear]);

  const handleBlur = ({ relatedTarget }) => {
    if (
      !relatedTarget ||
      !relatedTarget.dataset.dateinputidentifier ||
      !relatedTarget.dataset.dateinputidentifier.includes('uniqIdentifier')
    ) {
      form.setFieldTouched(field.name);
    }
  };

  const handleFocus = ({ target }) => {
    if (
      structure.every(({ value }) => value === '') &&
      refFirstInput.current &&
      target !== refFirstInput.current
    ) {
      refFirstInput.current.focus();
    }
  };

  const dataCsMask = pick(props, 'data-cs-mask');
  const classes = getInputClasses({
    hasHeight: true,
    hasValidationStatus: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  return (
    <fieldset className={className}>
      {!!label && (
        <Legend
          className="mx-15 mb-5"
          isDisabled={isDisabled}
          isRequired={isRequired}
          label={label}
          labelAdditionalInfo={labelAdditionalInfo}
        />
      )}
      <div
        className={classnames(classes, 'direction-ltr relative isolate flex w-full items-center')}
        data-testid={dataTestId ? `wrapper-${dataTestId}` : undefined}
      >
        <div className="relative isolate flex flex-1 items-stretch">
          {structure.map(({ name, handleChange, value, length, placeholder, title }, key) => (
            <input
              aria-describedby={errorNoteId}
              autoComplete="off"
              className={classnames(
                air360ClassName,
                'h-48 w-full overflow-hidden bg-transparent py-10 text-center text-16 leading-normal outline-none sm:text-14',
                {
                  'cursor-not-allowed opacity-100': isDisabled,
                },
              )}
              data-dateinputidentifier={uniqIdentifier}
              {...dataCsMask}
              data-testid={`${dataTestId || kebabCase(field.name)}-${title.toLowerCase()}`}
              disabled={isDisabled}
              key={name}
              maxLength={length}
              name={name}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={handleFocus}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              ref={key === 0 ? refFirstInput : undefined}
              style={{
                ...(isDisabled
                  ? {
                      WebkitTextFillColor: '#a0a0a0',
                    }
                  : {}),
              }}
              title={title}
              type="tel"
              value={value}
            />
          ))}
          <BirthDateInputSeparator
            isDisabled={isDisabled}
            isErrored={isErrored}
            isValidated={isValidated}
            position={1}
          />
          <BirthDateInputSeparator
            isDisabled={isDisabled}
            isErrored={isErrored}
            isValidated={isValidated}
            position={2}
          />
        </div>
        {!isDisabled && (
          <ImageValidationStatus
            className="absolute end-0 mx-20"
            isErrored={isErrored}
            isValidated={isValidated}
          />
        )}
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          className="mx-15"
          errorMessage={errorMessage ?? get(form.errors, field.name)}
          errorNoteId={errorNoteId}
          isErrored={isErrored}
        />
      )}
    </fieldset>
  );
};
